import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import VehicleCard from '../../components/VehicleCard';
import { productAPI } from '../../config/api';
import swal from 'sweetalert';
import AdminSidebar from '../../components/AdminSidebar';
import { showAppToast } from '../../utils/notifications';

function AdminProduct({ products, onUpdate }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [sortMode, setSortMode] = useState('Name');
  const [loading, setLoading] = useState(false);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return (products || [])
      .filter((product) => (
        product.name?.toLowerCase().includes(term) ||
        product.description?.toLowerCase().includes(term) ||
        product.categoryName?.toLowerCase().includes(term) ||
        product.bookingStatus?.toLowerCase().includes(term)
      ))
      .filter((product) => (
        statusFilter === 'All statuses' ||
        (product.bookingStatus || 'Available') === statusFilter
      ))
      .sort((first, second) => {
        if (sortMode === 'Highest price') return Number(second.price || 0) - Number(first.price || 0);
        if (sortMode === 'Lowest price') return Number(first.price || 0) - Number(second.price || 0);
        if (sortMode === 'Newest model') return Number(second.year || second.modelYear || 0) - Number(first.year || first.modelYear || 0);
        return (first.name || '').localeCompare(second.name || '');
      });
  }, [products, searchTerm, sortMode, statusFilter]);

  const statusOptions = useMemo(() => (
    ['All statuses', ...new Set((products || []).map((product) => product.bookingStatus || 'Available'))]
  ), [products]);

  const handleDelete = async (id, name) => {
    const result = await swal({
      title: 'Delete Vehicle?',
      text: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });

    if (!result) return;

    setLoading(true);
    try {
      await productAPI.delete(id);
      showAppToast({ message: 'Vehicle deleted successfully', type: 'success', duration: 2400 });
      onUpdate();
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to delete vehicle', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
      <div className="mx-auto max-w-7xl">
        <div className="admin-header">
          <div>
            <p className="text-accent">Fleet</p>
            <h1 className="section-title text-4xl text-primary-theme">Admin Vehicles</h1>
            <p className="text-secondary-theme">Total: {products?.length || 0} vehicles</p>
          </div>
          <Link to="/admin/vehicle/new" className="btn-luxury">
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Add Vehicle
          </Link>
        </div>

        <input
            type="search"
            placeholder="Search by vehicle, category, or status..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="admin-search"
          />
          <div className="admin-filter-strip">
            <select
              aria-label="Filter vehicles by status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {statusOptions.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
            <select
              aria-label="Sort vehicles"
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
            >
              <option>Name</option>
              <option>Newest model</option>
              <option>Highest price</option>
              <option>Lowest price</option>
            </select>
          </div>

        {filteredProducts.length ? (
          <div className="admin-fleet-grid">
            {filteredProducts.map((product) => (
              <div key={product.id} className="admin-vehicle-card card-luxury">
                <VehicleCard product={product} role="admin" hideDefaultFooter />
                <div className="admin-vehicle-card-footer">
                  <span>{product.categoryName || 'Uncategorized'}</span>
                </div>
                <div className="admin-vehicle-actions" aria-label={`Actions for ${product.name}`}>
                  <Link
                    to={`/vehicle/show/${product.id}/${encodeURIComponent(product.name)}`}
                    className="admin-icon-action admin-icon-action--view"
                    aria-label={`View ${product.name}`}
                    title={`View ${product.name}`}
                  >
                    <i className="bi bi-eye-fill" aria-hidden="true" />
                  </Link>
                  <Link
                    to={`/admin/vehicle/${product.id}`}
                    className="admin-icon-action"
                    aria-label={`Edit ${product.name}`}
                    title={`Edit ${product.name}`}
                  >
                    <i className="bi bi-pencil-square" aria-hidden="true" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDelete(product.id, product.name)}
                    disabled={loading}
                    className="admin-icon-action admin-icon-action--danger"
                    aria-label={`Delete ${product.name}`}
                    title={`Delete ${product.name}`}
                  >
                    <i className="bi bi-trash3-fill" aria-hidden="true" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-luxury rounded-lg p-10 text-center text-secondary-theme">
            {searchTerm ? 'No vehicles found matching your search' : 'No vehicles yet. Add your first vehicle.'}
          </div>
        )}
      </div>
    </div>
    </div>

  );
}

export default AdminProduct;
